import Icon from "components/Icons/Icon";
import { motion } from "framer-motion";
// The route screen, the product's signature view. Fresh Japan render (shots.so) that
// replaces the shared `app-route.webp` for the hero only, so the header can carry its own
// larger mockup (123yb691nzg). FeatureToolboxSection's fan still uses `app-route.webp`.
import PhoneMockup from "img/hero-phone-japan.webp";
import { useEffect, useState } from "react";

// Same brand "S" mark used by BlogBanner.tsx's S_MARK_PATH, kept in its
// native 81x108 coordinate space so the SVG viewBox scales it automatically.
const S_PATH =
	"M17.3912 99.1864C14.9217 99.1864 12.9613 101.159 13.0092 103.593C13.0589 106.025 15.0991 108 17.5668 108C20.0363 108 21.9967 106.025 21.9488 103.593C21.8991 101.159 19.8589 99.1864 17.3912 99.1864ZM65.3336 33.3614C76.1235 30.4927 80.4221 24.1043 80.431 16.7419C80.4434 6.75566 72.3519 0 59.0871 0C38.1939 0 0 22.4846 0 43.1187C0 59.3036 21.0387 64.5548 35.3004 67.8972C49.5639 71.2377 60.588 74.2732 60.588 78.2187C60.588 82.1625 48.0081 91.0523 27.7464 97.4815C25.575 98.2036 24.3065 100.144 24.9097 101.816C25.5147 103.487 27.7145 104.079 29.9356 103.533C38.3749 101.452 78.201 92.3474 78.201 74.4204C78.201 56.4935 35.2862 49.1612 35.2862 45.9005C35.2862 42.5528 56.6017 35.6819 65.3336 33.3614Z";

// The same S, as a CSS mask. Derived from S_PATH rather than a second copy of the path data —
// two hand-kept copies of a 2 kB path is a drift waiting to happen. The viewBox matches the
// on-screen <svg> below (-3.3 -3.9 87 116) so the mask and the gradient-filled clip S share
// one coordinate space; paired with `mask-size: contain` / `mask-position: center` in CSS it
// reproduces the SVG's `preserveAspectRatio="xMidYMid meet"` and the two S shapes register.
const S_MASK_URL = `data:image/svg+xml;utf8,${encodeURIComponent(
	`<svg xmlns="http://www.w3.org/2000/svg" viewBox="-3.3 -3.9 87 116"><path d="${S_PATH}" transform="rotate(-14 40.5 54)" fill="#000"/></svg>`
)}`;

// Looping travel b-roll clipped into the Stippl "S" — sits behind the tile
// stage as a decorative accent, same treatment as the marketing redesign's
// hero background.
//
// Self-hosted and re-encoded (123yb6937gw). This used to hot-link
// `videos.pexels.com/.../8356893-uhd_2560_1440_25fps.mp4`: 2560x1440, 16.3 s, **21.3 MB**,
// autoplaying on the page that already fails LCP, and fetched on mobile too. For a clip that is
// masked into a letterform about 400 px wide.
//
// Now 960x540, trimmed to 10 s, served from our own origin so we own its cache headers:
// 337 KB VP9 / 591 KB H.264, from 21.3 MB. Pexels' licence permits the clip; their guidance is
// against hot-linking their CDN for production, which this also stops.
//
// Versioned filenames because `public/` assets are served without a content hash — changing the
// clip means bumping `-v1`, not overwriting, or caches keep the old one.
const HERO_VIDEO_WEBM = "/hero-s-v1.webm";
const HERO_VIDEO_MP4 = "/hero-s-v1.mp4";
const HERO_VIDEO_POSTER = "/hero-s-v1-poster.jpg";

// The clip's own dimensions. On the <video> they reserve the box before the first frame decodes,
// which is one of the unsized elements behind the 0.86 desktop CLS (123yb6937gy).
const HERO_VIDEO_W = 960;
const HERO_VIDEO_H = 540;

/**
 * Whether the visitor has asked for less motion.
 *
 * A looping background video is exactly what `prefers-reduced-motion` is about, and honouring it
 * also means those visitors never download the clip at all — the poster alone is painted. Read
 * through the same matchMedia pattern as `useIsMobile` below, including the SSR guard: there is
 * no `window` while the marketing bundle renders on the server.
 */
const useReducedMotion = () => {
	const [reduced, setReduced] = useState(false);
	useEffect(() => {
		if (typeof window === "undefined" || !window.matchMedia) return undefined;
		const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
		const update = () => setReduced(mq.matches);
		update();
		mq.addEventListener("change", update);
		return () => mq.removeEventListener("change", update);
	}, []);
	return reduced;
};

// Same 440x580 stage + per-tile position/size/color/animation values as the
// redesign's Hero() — left column (itinerary planner/bed/packing list), right
// column (travel tracker/AI planner/travel reel).
type Tile = {
	icon: React.ComponentProps<typeof Icon>["name"];
	size: number;
	top: number;
	left?: number;
	right?: number;
	bg: string;
	accent: string;
	rot: number;
	rotAmp: number;
	dy: number;
	delay: number;
};

const FLOAT_TILES: Tile[] = [
	{ icon: "calendar--chunky", size: 66, top: 45, left: 14, bg: "#fff2e6", accent: "#e8954a", rot: -8, rotAmp: 3, dy: -8, delay: 0 },
	{ icon: "bed--chunky", size: 60, top: 250, left: 26, bg: "#e5f8f3", accent: "#13c892", rot: 6, rotAmp: 2, dy: 7, delay: 1.2 },
	{ icon: "backpack--chunky", size: 58, top: 420, left: 10, bg: "#fdeef3", accent: "#e84d7e", rot: -5, rotAmp: 3, dy: -7, delay: 0.6 },
	{ icon: "photo--chunky", size: 64, top: 90, right: 12, bg: "#f1ecfd", accent: "#7c3aed", rot: 7, rotAmp: 2, dy: 8, delay: 0.5 },
	{ icon: "sparkle--chunky", size: 60, top: 290, right: 24, bg: "#f1ecfd", accent: "#7c3aed", rot: -6, rotAmp: 3, dy: -8, delay: 1.6 },
	{ icon: "video-camera--chunky", size: 56, top: 450, right: 8, bg: "#e5f8f3", accent: "#13c892", rot: 5, rotAmp: 2, dy: 6, delay: 0.9 },
];

// Freeze the floating drift and drop the <foreignObject> video on phones:
//  - on a small screen the per-tile/phone drift reads as random jitter rather
//    than a gentle float (ClickUp 86cb932hg);
//  - iOS Safari does not paint HTML (incl. <video>) inside an SVG <foreignObject>
//    that sits under a clipPath, so the S had nothing inside it and no visible
//    silhouette at all on mobile. A flat gradient fill on the same path keeps
//    the mark visible on every device.
const useIsMobile = () => {
	const [isMobile, setIsMobile] = useState(false);
	useEffect(() => {
		if (typeof window === "undefined" || !window.matchMedia) return undefined;
		const mq = window.matchMedia("(max-width: 768px)");
		const update = () => setIsMobile(mq.matches);
		update();
		mq.addEventListener("change", update);
		return () => mq.removeEventListener("change", update);
	}, []);
	return isMobile;
};

const HeroShowcase = () => {
	const isMobile = useIsMobile();
	const reducedMotion = useReducedMotion();

	return (
		<div className="intro-header__showcase">
			<div className="intro-header__s-window" aria-hidden="true">
				<svg width="100%" height="100%" viewBox="-3.3 -3.9 87 116" preserveAspectRatio="xMidYMid meet">
					<defs>
						{/* Rotate only the clip shape (same "uprightPhoto" trick as the redesign's
						    SVideoWindow) so the S silhouette tilts but the video inside stays level. */}
						<clipPath id="hero-s-clip">
							<path d={S_PATH} transform="rotate(-14 40.5 54)" />
						</clipPath>
						{/* Always-painted base fill for the S so the silhouette is visible even
						    when the video layer is absent (mobile) or still loading. */}
						<linearGradient id="hero-s-fill" x1="0" y1="0" x2="0" y2="1">
							<stop offset="0" stopColor="#13c892" />
							<stop offset="1" stopColor="#00b3d4" />
						</linearGradient>
						{/* Same fadeBottom technique as the redesign's SVideoWindow: an unrotated
						    gradient overlay inside the clip fades the S/video into the section
						    background (#eef6f8) toward the bottom instead of cutting off sharply. */}
						<linearGradient id="hero-s-fade-bottom" x1="0" y1="0" x2="0" y2="1">
							<stop offset="0.55" stopColor="#eef6f8" stopOpacity="0" />
							<stop offset="1" stopColor="#eef6f8" stopOpacity="1" />
						</linearGradient>
					</defs>
					<g clipPath="url(#hero-s-clip)">
						<rect x="-3.3" y="-3.9" width="87" height="116" fill="url(#hero-s-fill)" />
						{/* Desktop keeps the <foreignObject> video. Phones get the same video as a
						    masked HTML element outside the SVG instead — see the note above
						    `useIsMobile`: iOS Safari will not paint HTML inside a <foreignObject>
						    under a clipPath, which is why this was dropped and the S went flat
						    (86cbbben2 asked for it back).

						    The gradient <rect> above stays painted underneath either way, so a
						    browser that renders neither the foreignObject nor the mask still shows
						    a solid S rather than a hole — which is the failure this had before. */}
						{!isMobile && (
							<foreignObject x="-3.3" y="-3.9" width="87" height="116">
								{reducedMotion ? (
									<img className="intro-header__s-video" src={HERO_VIDEO_POSTER} alt="" width={HERO_VIDEO_W} height={HERO_VIDEO_H} />
								) : (
									<video
										className="intro-header__s-video"
										poster={HERO_VIDEO_POSTER}
										width={HERO_VIDEO_W}
										height={HERO_VIDEO_H}
										autoPlay
										muted
										loop
										playsInline
									>
										{/* WebM first: VP9 is 337 KB against H.264's 591 KB, so every browser that can
										    take it does. Safari falls through to the MP4. */}
										<source src={HERO_VIDEO_WEBM} type="video/webm" />
										<source src={HERO_VIDEO_MP4} type="video/mp4" />
									</video>
								)}
							</foreignObject>
						)}
						<rect x="-3.3" y="-3.9" width="87" height="116" fill="url(#hero-s-fade-bottom)" />
					</g>
				</svg>
				{isMobile &&
					(reducedMotion ? (
						<img
							className="intro-header__s-video-mobile"
							style={{ ["--s-mask" as string]: `url("${S_MASK_URL}")` } as React.CSSProperties}
							src={HERO_VIDEO_POSTER}
							width={HERO_VIDEO_W}
							height={HERO_VIDEO_H}
							alt=""
							aria-hidden="true"
						/>
					) : (
						<video
							className="intro-header__s-video-mobile"
							style={{ ["--s-mask" as string]: `url("${S_MASK_URL}")` } as React.CSSProperties}
							poster={HERO_VIDEO_POSTER}
							width={HERO_VIDEO_W}
							height={HERO_VIDEO_H}
							autoPlay
							muted
							loop
							playsInline
							aria-hidden="true"
						>
							<source src={HERO_VIDEO_WEBM} type="video/webm" />
							<source src={HERO_VIDEO_MP4} type="video/mp4" />
						</video>
					))}
			</div>

			<div className="intro-header__tile-stage">
				{FLOAT_TILES.map((tile, i) => (
					<motion.div
						key={tile.icon}
						className="intro-header__float-tile"
						style={{
							top: tile.top,
							left: tile.left,
							right: tile.right,
							width: tile.size,
							height: tile.size,
							borderRadius: Math.round(tile.size * 0.28),
							background: tile.bg,
							boxShadow: `0 10px 28px ${tile.accent}40, 0 3px 10px ${tile.accent}20`,
							color: tile.accent,
							...(isMobile ? { transform: `rotate(${tile.rot}deg)` } : null),
						}}
						{...(isMobile
							? {}
							: {
									animate: { y: [0, tile.dy, 0], rotate: [tile.rot, tile.rot + tile.rotAmp, tile.rot - tile.rotAmp, tile.rot] },
									transition: { duration: 2.8 + i * 0.3, repeat: Infinity, ease: "easeInOut", delay: tile.delay },
								})}
					>
						<Icon name={tile.icon} size={Math.round(tile.size * 0.44)} fill={tile.accent} />
					</motion.div>
				))}

				<div className="intro-header__phone-wrap">
					<motion.div
						style={isMobile ? { transform: "rotate(5deg)" } : undefined}
						{...(isMobile
							? {}
							: { animate: { y: [0, -10, -4, 0], x: [0, 6, -4, 0], rotate: [5, 7, 3, 5] }, transition: { duration: 5, repeat: Infinity, ease: "easeInOut" } })}
					>
						{/* The LCP element on desktop, and it was competing with ~40 lazy tiles for bandwidth with
						    no priority hint at all (123yb6937gx). `fetchpriority` is spelled lowercase through a
						    cast because that is what this codebase already does for the blog spotlight image;
						    React 18 does not type the camelCase form.

						    No <link rel="preload"> to go with it: the file is a hashed bundle asset
						    (assets/hero-phone-japan-<hash>.webp), so there is no stable URL to name in the
						    static template. Preloading it properly needs the build to emit the tag. */}
						<img src={PhoneMockup} alt="Stippl app" className="intro-header__phone-img" {...({ fetchpriority: "high" } as any)} />
					</motion.div>
				</div>
			</div>
		</div>
	);
};

export default HeroShowcase;
